const STORAGE_KEY = 'petri:workspace';

export interface StoredGraph {
  id: string;
  name: string;
  data: string; // base64-encoded gzip bytes from Graph.dump()
  version: number;
  lastModified: number;
}

export interface Workspace {
  version: 1;
  graphs: StoredGraph[];
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

function createEmptyWorkspace(): Workspace {
  return {
    version: 1,
    graphs: [],
  };
}

export function loadWorkspace(): Workspace {
  if (!process.env.CORGI_FOR_BROWSER) {
    return createEmptyWorkspace();
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return createEmptyWorkspace();
    }
    const parsed = JSON.parse(raw) as Workspace;
    if (parsed.version !== 1) {
      console.warn('Unknown workspace version, resetting');
      return createEmptyWorkspace();
    }
    return parsed;
  } catch (e) {
    console.error('Failed to load workspace', e);
    return createEmptyWorkspace();
  }
}

export function saveWorkspace(workspace: Workspace): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(workspace));
  } catch (e) {
    console.error('Failed to save workspace', e);
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function saveGraph(
  id: string | undefined,
  name: string,
  dumpBytes: Uint8Array,
  version: number,
): Promise<string> {
  const workspace = loadWorkspace();
  const graphId = id ?? generateId();
  const data = bytesToBase64(dumpBytes);

  const existingIndex = workspace.graphs.findIndex(g => g.id === graphId);
  const storedGraph: StoredGraph = {
    id: graphId,
    name,
    data,
    version,
    lastModified: Date.now(),
  };

  if (existingIndex >= 0) {
    workspace.graphs[existingIndex] = storedGraph;
  } else {
    workspace.graphs.push(storedGraph);
  }

  saveWorkspace(workspace);
  return graphId;
}

export function loadGraphData(
  id: string,
): {name: string; bytes: Uint8Array; version: number} | undefined {
  const workspace = loadWorkspace();
  const graph = workspace.graphs.find(g => g.id === id);
  if (!graph) {
    return undefined;
  }
  return {
    name: graph.name,
    bytes: base64ToBytes(graph.data),
    version: graph.version,
  };
}

export function deleteGraph(id: string): void {
  const workspace = loadWorkspace();
  workspace.graphs = workspace.graphs.filter(g => g.id !== id);
  saveWorkspace(workspace);
}

export function listGraphs(): Array<{
  id: string;
  name: string;
  lastModified: number;
}> {
  const workspace = loadWorkspace();
  return workspace.graphs
    .map(g => ({
      id: g.id,
      name: g.name,
      lastModified: g.lastModified,
    }))
    .sort((a, b) => b.lastModified - a.lastModified);
}

export function exportWorkspace(): string {
  const workspace = loadWorkspace();
  return JSON.stringify(workspace, null, 2);
}

export function importWorkspace(json: string): void {
  try {
    const parsed = JSON.parse(json) as Workspace;
    if (parsed.version !== 1) {
      throw new Error('Unknown workspace version');
    }
    saveWorkspace(parsed);
  } catch (e) {
    console.error('Failed to import workspace', e);
    throw e;
  }
}
